import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { ContactCta } from "@/components/common/DetailPage";
import { imageFor } from "@/lib/imagery";
import { COMPANY } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

const POSITIONING_KEYS = [
  "positioning",
  "jurisdiction",
  "sectors",
  "compliance",
] as const;

// NOTE: KA is scaffolded as an English mirror pending Georgian translation.
const POSITIONING_EN: Array<{
  eyebrow: string;
  title: string;
  body: string;
  link?: { to: RouteKey; label: string };
}> = [
  {
    eyebrow: "01 — Positioning",
    title: "International trade and logistics from Georgia.",
    body: "BNM Group LLC acts as a commercial counterparty for the physical trade of grains, food products and petrochemicals, connecting qualified suppliers with institutional buyers.",
  },
  {
    eyebrow: "02 — Location",
    title: "A gateway between Europe, Türkiye, the Caucasus and Central Asia.",
    body: "Operating from Georgia at the crossroads of major trade corridors, with access to Black Sea ports and regional road and rail routes.",
  },
  {
    eyebrow: "03 — Sectors",
    title: "Three commodity verticals, one counterparty.",
    body: "Grains & cereals, food products and petrochemicals — traded and supplied through a single commercial and documentary interface, with sector-specific process discipline.",
    link: { to: "commodities.index", label: "See sectors" },
  },
  {
    eyebrow: "04 — Compliance",
    title: "A compliance-led commercial approach.",
    body: "Counterparty verification and an anti-fraud notice apply to commercial engagement before any agreement is finalised.",
    link: { to: "compliance.index", label: "Compliance & Trust" },
  },
];

const POSITIONING: Record<"en" | "ka", typeof POSITIONING_EN> = {
  en: POSITIONING_EN,
  ka: POSITIONING_EN,
};

const OFFICIAL_EN: Array<{ key: string; label: string; body: string }> = [
  {
    key: "profile",
    label: "Corporate profile",
    body: "BNM Group LLC is an international trading company registered in Georgia, active in international trade, commodity trading, industrial supply, global sourcing and logistics across grains & cereals, food products and petrochemical products.",
  },
  {
    key: "governance",
    label: "Registration",
    body: "Registered in Georgia as a Limited Liability Company (LLC) under identification code 404945841, established 2012, with its registered office in Tbilisi, Georgia.",
  },
  {
    key: "scope",
    label: "Operational scope",
    body: "The company operates internationally, positioned in Georgia as a gateway between Europe, Türkiye, the Caucasus and Central Asia.",
  },
];

const OFFICIAL: Record<"en" | "ka", typeof OFFICIAL_EN> = {
  en: OFFICIAL_EN,
  ka: OFFICIAL_EN,
};

export default function About() {
  const { t, path, locale } = useT();
  const img = imageFor("about");
  const items = POSITIONING[locale];

  return (
    <>
      <SEO routeKey="about" />
      <PageHero routeKey="about" imageUrl={img?.url} imageAlt={img?.alt} />

      {/* Positioning grid — 4 structural attributes, no invented copy */}
      <section className="border-b border-bone-500" aria-labelledby="about-positioning">
        <div className="container-bnm py-20 md:py-28">
          <h2 id="about-positioning" className="sr-only">
            {locale === "ka" ? "Positioning" : "Positioning"}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-bone-500">
            {items.map((it, idx) => (
              <li
                key={POSITIONING_KEYS[idx]}
                className="border-r border-b border-bone-500 p-8 md:p-12 flex flex-col gap-5"
                data-testid={`about-${POSITIONING_KEYS[idx]}`}
              >
                <p className="eyebrow">{it.eyebrow}</p>
                <h3 className="font-display font-light text-2xl md:text-3xl leading-tight text-ink">
                  {it.title}
                </h3>
                <p className="text-slate2 leading-relaxed text-[15px] max-w-[52ch]">
                  {it.body}
                </p>
                {it.link && (
                  <Link
                    to={path(it.link.to)}
                    className="mt-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-ink hover:text-clay transition-colors border-b border-ink hover:border-clay pb-0.5 self-start"
                    data-testid={`about-link-${POSITIONING_KEYS[idx]}`}
                  >
                    {it.link.label}
                    <ArrowRight size={12} />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Official-content section — placeholders pending verified company data */}
      <section className="border-b border-bone-500 bg-bone-300/40" aria-labelledby="about-official">
        <div className="container-bnm py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">05 — {t.sections.overviewLabel}</p>
            <h2 id="about-official" className="font-display font-light text-3xl md:text-4xl text-ink leading-tight">
              {locale === "ka" ? "Official content" : "Official content"}
            </h2>
            <p className="mt-6 text-xs uppercase tracking-eyebrow font-mono text-slate2 max-w-[38ch]">
              {COMPANY.legalName}
            </p>
          </div>
          <div className="lg:col-span-8 flex flex-col gap-10">
            {OFFICIAL[locale].map((block) => (
              <div
                key={block.key}
                className="border-l-2 border-ink pl-6 py-2"
                data-testid={`about-official-${block.key}`}
              >
                <p className="eyebrow mb-2">{block.label}</p>
                <p className="text-ink text-lg md:text-xl font-display font-light leading-snug max-w-prose">
                  {block.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
